import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { type JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { TableAndFiltersContainer } from "features/layout/components";

import { ColumnsVisibilityControl } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useDebounce from "common/hooks/useDebounce";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import {
  DEFAULT_AUDIENCE_FILTERS,
  NEW_CAMPAIGN_AUDIENCE_COLUMNS,
} from "../../../../constants";
import { useCampaignContext } from "../../../../context/CampaignContext";
import useAudienceSearch from "../../../../datahooks/useAudienceSearch";
import useTargeting from "../../../../datahooks/useTargeting";
import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import useAudienceFilters from "../../../../hooks/useAudienceFilters";
import ManageMobileAudience from "../../ManageAudience/ManageMobileAudience";
import Stepper from "../Stepper";
import StepperNextButton from "../StepperNextButton";
import AudienceDetails from "./AudienceDetails";
import AudienceHeader from "./AudienceHeader";
import AudienceProgress from "./AudienceProgress";
import AudienceDesktopFilters from "./DesktopLayout/AudienceDesktopFilters";
import AudienceDesktopTable from "./DesktopLayout/AudienceDesktopTable";
import RemoveAudienceDialog from "./DesktopLayout/RemoveAudienceDialog";
import ExportAllAudience from "./ExportAllAudience";
import AudienceMobileList from "./MobileLayout/AudienceMobileList";
import MobileAudienceFilters from "./MobileLayout/MobileAudienceFilters";
import useAudienceProgress from "./useAudienceProgress";

import type { AudienceProfile, AudienceStats } from "../../../../types";

import NoTargetsFoundImage from "assets/images/empty-placeholders/no-profiles.png";
import ProgressImage from "assets/images/empty-placeholders/progress.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

interface AudienceProps {
  audienceStats: AudienceStats;
  openLeadPreview: (leadId: string) => void;
}

export default function Audience({
  audienceStats,
  openLeadPreview,
}: AudienceProps) {
  const navigate = useNavigate();
  const isDesktop = useTwBreakpoint("lg");
  const { type: campaignType, target_count: targetCount } =
    useCampaignContext();
  const { t } = useTranslation("campaigns", { keyPrefix: "audience" });

  const { removeTargeting, isRemovingTargeting } = useTargetingMutations();
  const { targeting } = useTargeting();

  const [filters, setFilters] = useState(DEFAULT_AUDIENCE_FILTERS);
  const [selectedProfiles, setSelectedProfiles] = useState<AudienceProfile[]>(
    [],
  );

  const { page, setPage, pageSize, setPageSize } = usePagination(
    "create-campaign-audience",
  );

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "create-campaign-audience",
    NEW_CAMPAIGN_AUDIENCE_COLUMNS,
  );

  function resetSelectedProfiles() {
    setSelectedProfiles([]);
  }

  const { resetAllFilters } = useAudienceFilters(filters, setFilters);

  const { keyword, ...audienceFilters } = filters;
  const searchValue = keyword;
  const [debouncedSearchValue] = useDebounce(searchValue, 300);

  const audienceSearchFilters = useMemo(
    () => ({
      ...filters,
      keyword: debouncedSearchValue,
    }),
    [filters, debouncedSearchValue],
  );

  const hasActiveSearchParams =
    Object.values(audienceFilters).some((filter) =>
      Array.isArray(filter) ? !!filter.length : !!filter,
    ) || debouncedSearchValue;

  useEffect(() => {
    setPage(0);
  }, [audienceSearchFilters]);

  const {
    audience,
    audienceTotal,
    isLoadingAudience,
    isFetchingAudience,
    isAudienceError,
    refetchAudience,
    audienceFacets,
  } = useAudienceSearch(audienceSearchFilters, { page, pageSize });

  const isMiningTargets = audienceStats && audienceStats.progress !== 100;

  useEffect(() => {
    setSelectedProfiles([]);
  }, [filters]);

  useAudienceProgress(audienceSearchFilters, isMiningTargets, {
    page,
    pageSize,
  });

  const isList = campaignType === "list" || campaignType === "crm";
  const isEmptyCampaign =
    isList &&
    targeting &&
    "profiles" in targeting &&
    !targeting.profiles.length &&
    targetCount === 0 &&
    !isLoadingAudience &&
    !audience?.length;
  const totalItemCount = audienceTotal || 0;

  if (isAudienceError) {
    return (
      <ErrorPlaceholder
        errorMessage="Error loading audience"
        onRetry={refetchAudience}
      />
    );
  }

  const desktopFiltersComponent = (
    <AudienceDesktopFilters
      filters={filters}
      setFilters={setFilters}
      audienceFacets={audienceFacets}
      isLoadingFacets={isLoadingAudience}
      isMiningTargets={isMiningTargets}
    />
  );

  const filterEmptyStateComponent = (
    <EmptyPlaceholder
      imageSrc={NoResultsImage}
      title="No results found"
      subtitle="There were no results that match your criteria"
      actionComponent={
        <Button
          leftIcon={<ArrowCounterClockwise />}
          variant="secondary-black"
          onClick={resetAllFilters}
        >
          Clear
        </Button>
      }
    />
  );

  const commonProps = {
    rows: audience,
    isLoading: isFetchingAudience,
    pageSize,
    totalItemCount,
    selectedProfiles,
    setSelectedProfiles,
    openLeadPreview,
    emptyStateComponent: filterEmptyStateComponent,
  };

  let mainComponent: JSX.Element;

  if (isEmptyCampaign) {
    mainComponent = (
      <EmptyPlaceholder
        title="No targets found"
        subtitle="This campaign does not have any targets. You can add targets through integrations, via API, or manually."
        imageSrc={NoTargetsFoundImage}
      />
    );
  } else if (isMiningTargets && !audience) {
    mainComponent = (
      <EmptyPlaceholder
        title={t("loadingTargetsTitle")}
        subtitle={t("loadingTargetsSubtitle")}
        imageSrc={ProgressImage}
      />
    );
  } else if (isLoadingAudience || audience?.length) {
    const listComponent = isDesktop ? (
      <AudienceDesktopTable
        {...commonProps}
        filters={audienceSearchFilters}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
      />
    ) : (
      <AudienceMobileList {...commonProps} />
    );

    mainComponent = (
      <div className="flex w-full grow flex-col gap-y-4">
        {listComponent}
        <PaginationFooter
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedPage={page}
          setSelectedPage={setPage}
          totalItemCount={totalItemCount}
          isLastPageHidden
        />
      </div>
    );
  } else if (hasActiveSearchParams) {
    mainComponent = filterEmptyStateComponent;
  } else {
    mainComponent = (
      <EmptyPlaceholder
        title={t("noTargetsFoundTitle")}
        subtitle={t("noTargetsFoundSubtitle")}
        imageSrc={NoTargetsFoundImage}
        actionComponent={
          <div className="flex flex-col gap-2 lg:flex-row">
            <Button
              onClick={() => removeTargeting()}
              isLoading={isRemovingTargeting}
              size="sm"
            >
              Edit targeting
            </Button>
            <AudienceDetails audienceStats={audienceStats} />
          </div>
        }
      />
    );
  }

  const exportAllAudienceComponent = (
    <ExportAllAudience
      filters={filters}
      isMiningTargets={isMiningTargets}
      allAudienceCount={totalItemCount}
    />
  );

  const searchInputComponent = (
    <SearchInput
      className="lg:max-w-72"
      placeholder="Search audience"
      variant={isDesktop ? "sm" : "lg"}
      value={searchValue}
      onChange={(e) =>
        setFilters({ ...filters, keyword: e.currentTarget.value })
      }
      onClear={() => setFilters({ ...filters, keyword: "" })}
      disabled={isMiningTargets}
    />
  );

  const audienceContent = (
    <>
      {!isEmptyCampaign && (
        <AudienceProgress
          progress={audienceStats ? audienceStats.progress : 0}
        />
      )}

      {mainComponent}
    </>
  );

  return (
    <>
      <AudienceHeader
        isMiningTargets={isMiningTargets}
        filters={filters}
        setFilters={setFilters}
      />

      <div className="flex w-full grow flex-col gap-y-4">
        {isDesktop ? (
          <TableAndFiltersContainer
            searchComponent={searchInputComponent}
            actionsComponent={
              <>
                {selectedProfiles.length ? (
                  <RemoveAudienceDialog
                    selectedProfiles={selectedProfiles}
                    onSuccess={resetSelectedProfiles}
                    filters={audienceSearchFilters}
                  />
                ) : (
                  <AudienceDetails audienceStats={audienceStats} />
                )}

                {exportAllAudienceComponent}
              </>
            }
            filtersComponent={desktopFiltersComponent}
            columnSelectionComponent={
              <ColumnsVisibilityControl
                columns={NEW_CAMPAIGN_AUDIENCE_COLUMNS}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
              />
            }
          >
            {audienceContent}
          </TableAndFiltersContainer>
        ) : (
          <>
            <div className="flex flex-col justify-between gap-3">
              <div className="flex gap-x-2.5 w-full">
                {searchInputComponent}

                <MobileAudienceFilters
                  filters={filters}
                  setFilters={setFilters}
                  audienceFacets={audienceFacets}
                  isLoadingFacets={isLoadingAudience}
                  isMiningTargets={isMiningTargets}
                />
              </div>

              <div className="flex gap-2.5 w-full">
                <AudienceDetails audienceStats={audienceStats} />

                {selectedProfiles.length ? (
                  <ManageMobileAudience
                    selectedTargets={selectedProfiles}
                    resetSelectedTargets={resetSelectedProfiles}
                    filters={audienceSearchFilters}
                  />
                ) : (
                  exportAllAudienceComponent
                )}
              </div>
            </div>

            {audienceContent}
          </>
        )}
      </div>

      <Stepper
        selectedIndex={1}
        isMiningTargets={isMiningTargets}
        nextButtonComponent={
          <StepperNextButton
            disabled={!isList && (!audienceStats || !audienceStats.valid)}
            onClick={() => navigate("../flows")}
          />
        }
      />
    </>
  );
}
